import {
  AlertTriangle,
  Snowflake,
  Wrench,
  Droplets,
  Volume2,
  Wind,
  Thermometer,
  ClipboardList,
  Clock,
  Phone,
  Image as ImageIcon,
  FileText,
} from "lucide-react";
import { useSeo } from "@/lib/useSeo";
import PageHero from "@/components/site/PageHero";
import ServiceFAQ from "@/components/site/ServiceFAQ";
import RelatedServices from "@/components/site/RelatedServices";
import FinalCTA from "@/components/site/FinalCTA";
import AreaPills from "@/components/site/AreaPills";
import DemoNotice from "@/components/site/DemoNotice";

const HERO_IMG =
  "https://static.prod-images.emergentagent.com/jobs/51ddf5f6-e80c-4259-9ef6-c5393e7b5903/images/71d54c90826a94d279928daf40ede643ab55beaac8e7b5b511f1b72407351e92.png";

const ISSUES = [
  {
    icon: Snowflake,
    title: "AC not cooling",
    desc: "Thermostat says cool but the air feels warm — the most common SWFL summer call.",
  },
  {
    icon: Droplets,
    title: "Unit leaking water",
    desc: "Pooling water near the air handler usually points to a clogged condensate drain line.",
  },
  {
    icon: Volume2,
    title: "Strange noises",
    desc: "Rattling, buzzing, hissing, or clicking — different sounds, different culprits.",
  },
  {
    icon: Wind,
    title: "Weak airflow",
    desc: "One room cold, the next warm — often a filter, fan, or duct issue.",
  },
  {
    icon: Thermometer,
    title: "Thermostat issues",
    desc: "Schedule won't hold, system won't respond, or the screen is acting up.",
  },
  {
    icon: Wrench,
    title: "Drain line clog",
    desc: "Float switch tripped or water in the pan — Florida humidity catches up fast.",
  },
];

const WHAT_WE_LOOK_AT = [
  "Thermostat behavior and settings",
  "Air filter condition and sizing",
  "Indoor coil and blower performance",
  "Outdoor condenser and refrigerant levels",
  "Condensate drain line and float switch",
  "Electrical: capacitors, contactor, breakers",
  "Refrigerant lines and connections",
  "Airflow at supply vents and return",
];

const WHAT_TO_TELL = [
  {
    icon: ClipboardList,
    title: "What the system is doing (or not doing)",
    desc: "Cooling weakly, blowing warm air, short cycling, won't turn on, ice on the lines, etc.",
  },
  {
    icon: Clock,
    title: "When the issue started",
    desc: "Today, yesterday, gradually over the last week — timing helps narrow the diagnosis.",
  },
  {
    icon: Phone,
    title: "Best way and time to reach you",
    desc: "Call, text, email — and when you're available for a callback or appointment window.",
  },
  {
    icon: ImageIcon,
    title: "Photos if anything looks off",
    desc: "Ice, water, error codes on the thermostat, the model sticker on the unit — all useful.",
  },
  {
    icon: FileText,
    title: "Approximate system age",
    desc: "Even a rough age (under 5 years, 10–15 years, original to the house) helps a lot.",
  },
];

const FAQS = [
  {
    q: "What should I do if my AC stops cooling?",
    a: "First, confirm the thermostat is set to cool and the breaker hasn't tripped. Check that the filter isn't completely clogged and that the outdoor condenser is running. If the system is blowing warm air after 15–20 minutes, shut it off to avoid further damage and start a service request — the more detail you can share, the faster a contractor can diagnose it.",
  },
  {
    q: "How long does an AC repair typically take?",
    a: "Most common SWFL repairs — drain line clogs, capacitors, contactors, thermostat issues — can be diagnosed and fixed in a single visit once the technician arrives. Refrigerant or compressor issues sometimes need parts ordered, so timing depends on what's actually wrong.",
  },
  {
    q: "Is it safe to keep running an AC that's blowing warm air?",
    a: "Generally no — running a system that's not cooling can ice up the coil, burn out a compressor, or cause water damage from a backed-up drain line. If your AC isn't cooling, the safest move is to shut it down at the thermostat and start a service request.",
  },
  {
    q: "Why is my AC unit leaking water?",
    a: "In Florida, the most common cause is a clogged condensate drain line. Humidity pulls a lot of moisture out of the air, and that water needs a clear path out. If the line is clogged, water backs up into the pan or onto the floor. A drain line clear is one of the quickest repairs there is.",
  },
  {
    q: "Is this a real HVAC company?",
    a: 'Sun Chaser Cooling is a sample HVAC website created by PrismForge Digital. <a class="text-aqua-deep underline-offset-2 hover:underline" href="/demo">See how this demo works →</a>',
    html: true,
  },
];

export default function ACRepair() {
  useSeo({
    title:
      "AC Repair in Southwest Florida — start a clear service request | Sun Chaser Cooling Demo",
    description:
      "Demo AC repair page for Southwest Florida. AC not cooling, leaking, or making strange sounds? See how a smarter service request works — built by PrismForge Digital.",
  });

  return (
    <div data-testid="ac-repair-page">
      <PageHero
        eyebrow="AC Repair"
        breadcrumb={[
          { label: "Home", to: "/" },
          { label: "Services", to: "/services" },
          { label: "AC Repair" },
        ]}
        title="AC repair in Southwest Florida —"
        highlight="start a clear service request."
        subtitle="When the air goes warm, the system trips, or something just doesn't sound right — this is how to tell us what's happening so a contractor walks in already prepared."
        primaryCta={{
          label: "Request HVAC Service",
          to: "/request-service?intent=ac-repair",
        }}
        secondaryCta={{
          label: "Schedule Maintenance",
          to: "/request-service?intent=maintenance",
        }}
        image={HERO_IMG}
        imageAlt="Closeup of an outdoor AC condenser unit beside a Southwest Florida home — typical of the systems this service covers."
        variant="light"
        testId="ac-repair-hero"
      />

      {/* Common issues */}
      <section
        data-testid="ac-repair-issues-section"
        aria-labelledby="ac-issues-heading"
        className="relative bg-white py-20 sm:py-24"
      >
        <div className="container-page">
          <div className="max-w-2xl">
            <span className="pill">
              <AlertTriangle className="h-3 w-3 text-sun" />
              When you might need this
            </span>
            <h2
              id="ac-issues-heading"
              className="mt-4 font-display text-3xl font-bold leading-tight tracking-tight text-navy sm:text-4xl"
            >
              Common AC issues in Florida homes.
            </h2>
            <p className="mt-4 text-base text-navy/70 sm:text-lg">
              Six things homeowners describe most often when they call &mdash;
              and what each one usually means.
            </p>
          </div>

          <ul className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {ISSUES.map((it) => {
              const Icon = it.icon;
              const slug = it.title
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, "-")
                .replace(/(^-|-$)/g, "");
              return (
                <li
                  key={it.title}
                  data-testid={`ac-issue-${slug}`}
                  className="rounded-2xl border border-mist-line bg-white p-6 shadow-soft transition-transform hover:-translate-y-1"
                >
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-navy text-aqua">
                    <Icon className="h-4 w-4" />
                  </span>
                  <h3 className="mt-4 font-display text-lg font-bold leading-tight tracking-tight text-navy">
                    {it.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-navy/70">
                    {it.desc}
                  </p>
                </li>
              );
            })}
          </ul>
        </div>
      </section>

      {/* What we look at */}
      <section
        data-testid="ac-repair-included-section"
        aria-labelledby="ac-included-heading"
        className="relative bg-mist py-20 sm:py-24"
      >
        <div className="container-page grid grid-cols-1 items-start gap-12 lg:grid-cols-12 lg:gap-14">
          <div className="lg:col-span-5">
            <span className="pill">
              <ClipboardList className="h-3 w-3 text-aqua" />
              What we look at
            </span>
            <h2
              id="ac-included-heading"
              className="mt-4 font-display text-3xl font-bold leading-tight tracking-tight text-navy sm:text-4xl"
            >
              A full diagnostic, top to bottom.
            </h2>
            <p className="mt-4 max-w-lg text-base text-navy/70 sm:text-lg">
              An honest diagnosis comes from checking the whole system &mdash;
              not just whichever piece is making the most noise.
            </p>
          </div>
          <div className="lg:col-span-7">
            <ul className="rounded-2xl border border-mist-line bg-white shadow-soft">
              {WHAT_WE_LOOK_AT.map((line, i) => (
                <li
                  key={line}
                  className={`flex items-center justify-between gap-3 px-5 py-4 ${
                    i < WHAT_WE_LOOK_AT.length - 1
                      ? "border-b border-mist-line"
                      : ""
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-aqua/15 font-display text-xs font-bold text-aqua-deep">
                      {i + 1}
                    </span>
                    <p className="text-[15px] font-medium text-navy/85">
                      {line}
                    </p>
                  </div>
                  <span className="hidden text-[10px] font-semibold uppercase tracking-[0.16em] text-navy/40 sm:inline">
                    Checked
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* What to tell us */}
      <section
        data-testid="ac-repair-tell-us-section"
        aria-labelledby="ac-tell-heading"
        className="relative bg-white py-20 sm:py-24"
      >
        <div className="container-page">
          <div className="max-w-2xl">
            <span className="pill">
              <FileText className="h-3 w-3 text-aqua" />
              What to tell us
            </span>
            <h2
              id="ac-tell-heading"
              className="mt-4 font-display text-3xl font-bold leading-tight tracking-tight text-navy sm:text-4xl"
            >
              What to share when you request service.
            </h2>
            <p className="mt-4 text-base text-navy/70 sm:text-lg">
              The smart intake walks you through this &mdash; but the more
              detail you can share upfront, the faster (and more accurate) the
              diagnosis.
            </p>
          </div>
          <ul className="mt-12 grid grid-cols-1 gap-5 md:grid-cols-2">
            {WHAT_TO_TELL.map((it) => {
              const Icon = it.icon;
              return (
                <li
                  key={it.title}
                  className="flex items-start gap-4 rounded-2xl border border-mist-line bg-white p-6 shadow-soft"
                >
                  <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-navy text-aqua">
                    <Icon className="h-4 w-4" />
                  </span>
                  <div className="min-w-0">
                    <h3 className="font-display text-lg font-bold leading-tight tracking-tight text-navy">
                      {it.title}
                    </h3>
                    <p className="mt-2 text-sm leading-relaxed text-navy/70">
                      {it.desc}
                    </p>
                  </div>
                </li>
              );
            })}
          </ul>
          <div className="mt-8">
            <DemoNotice
              variant="dark"
              label="Demo intake flow — no real emergency dispatch."
            />
          </div>
        </div>
      </section>

      {/* SWFL context */}
      <section
        data-testid="ac-repair-swfl-section"
        aria-labelledby="ac-swfl-heading"
        className="relative bg-mist py-20 sm:py-24"
      >
        <div className="container-page max-w-4xl">
          <span className="pill">
            <span className="h-1.5 w-1.5 rounded-full bg-aqua" />
            Southwest Florida context
          </span>
          <h2
            id="ac-swfl-heading"
            className="mt-4 font-display text-3xl font-bold leading-tight tracking-tight text-navy sm:text-4xl"
          >
            Why SWFL AC systems have it harder.
          </h2>
          <div className="mt-5 space-y-4 text-base leading-relaxed text-navy/75 sm:text-lg">
            <p>
              Southwest Florida cooling seasons are long. Most systems run
              eight to ten months of the year, and rainy-season humidity puts
              constant pressure on coils, drain lines, and outdoor units that
              have to fight salt-air corrosion.
            </p>
            <p>
              That&rsquo;s why something as small as a clogged drain line or
              dirty filter shows up as a real comfort issue here much faster
              than it would up north. Catching things early &mdash; with a
              clear service request &mdash; is the whole game.
            </p>
          </div>
          <div className="mt-8">
            <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-navy/55">
              We&rsquo;re available across SWFL
            </p>
            <AreaPills className="mt-3" />
          </div>
        </div>
      </section>

      <ServiceFAQ
        title="Common AC repair questions."
        subtitle="A few things homeowners ask before booking a repair."
        faqs={FAQS}
        testId="ac-repair-faq"
      />

      <RelatedServices currentSlug="ac-repair" />

      <FinalCTA
        title="Ready to start a service request?"
        subtitle="Tell us what's happening — we'll show the contractor exactly what's going on before the callback."
      />
    </div>
  );
}
